<script setup>
import dayjs from 'dayjs'
import { ref, computed, watch, onMounted } from 'vue'
import { Dialog } from 'quasar'
import { getI18n } from '@/plugins/i18n'

import { usePlanningStore } from '@/stores/planning.store'
import { usePortalStore } from '@/stores/portal.store'
import { useWebSiteStore } from '@/stores/web-site.store'

import { useNotify } from '@/composables/notify'
import { useStepper } from '@/composables/stepper'
import { useIsUpdated } from '@/composables/object'
import { useNavigator } from '@/composables/navigator'
import { useEscapeClose } from '@/composables/keyboard'
import { useDetectDevice } from '@/composables/device'
import { useClone } from '@/composables/object'
import { useAllocationAvailability } from '@/composables/allocation-availability'

import NavigateBack from '@/components/shared/NavigateBack.vue'
import StepperNavigation from '@/components/shared/StepperNavigation.vue'
import ServiceSelector from '@/components/shared/ServiceSelector.vue'
import WarehouseSelector from '@/components/shared/WarehouseSelector.vue'
import ConfirmDialog from '@/components/shared/ConfirmDialog.vue'

const props = defineProps({
  modelValue: {
    type: Object,
    required: true,
  },
  warehouse: {
    type: Object,
    default: null,
  },
  service: {
    type: Object,
    default: null,
  },
  scheduledDate: {
    type: String,
    default: null,
  },
})

const i18n = getI18n()
const { isMobile } = useDetectDevice()
const { step, nextStep, prevStep, markAttempted, wasAttempted } = useStepper()
const { notifySuccess, notifyError } = useNotify()

const planningStore = usePlanningStore()
const portalStore = usePortalStore()
const webSiteStore = useWebSiteStore()

const localizedMovementTypes = planningStore.getLocalizedWarehouseMovementTypes()

const { loadAvailabilityAtDay } = useAllocationAvailability()

const canDelete = computed(() =>
  editItem.value.status !== planningStore.warehouseMovementStatusEnum.CONFIRMED &&
  !editItem.value.is_from_confirmed_allocation
)
const isAdjustment = computed(() => editItem.value.movement_type === planningStore.warehouseMovementTypeEnum.ADJUSTMENT)
const isTransfer = computed(() => editItem.value.movement_type === planningStore.warehouseMovementTypeEnum.TRANSFER)
const isSerialized = computed(() => planningStore.equipments.find(s => s.id === editItem.value.service)?.is_serialized)
const isSplitTransfer = computed(() => isTransfer.value
  && editItem.value.destination_scheduled_at
  && editItem.value.destination_scheduled_at !== editItem.value.scheduled_at)

const form = ref(null)
const editItem = ref(props.modelValue.id ? useClone(props.modelValue) : {
  warehouse: props.warehouse?.id || webSiteStore.getDefaultWarehouse(),
  service: props.service?.id || null,
  movement_type: planningStore.warehouseMovementTypeEnum.LOAD,
  quantity: 1,
  scheduled_at: null,
  destination_warehouse: null,
  destination_scheduled_at: null,
  transit_warehouse: webSiteStore.getDefaultTransitWarehouse(),
  project: null,
  notes: null,
  ...useClone(props.modelValue),
})
editItem.value.scheduled_at = editItem.value.scheduled_at
  ? dayjs(editItem.value.scheduled_at).format('YYYY-MM-DDTHH:mm')
  : dayjs(props.scheduledDate ?? undefined).format('YYYY-MM-DDTHH:mm')

const { isUpdated, isUpdating } = useIsUpdated(editItem)

const warehouses = ref([])
const serviceFilter = ref('')
const physicalServices = computed(() => {
  let services = planningStore.equipments.filter(s => s.structure_type === portalStore.serviceStructureTypeEnum.PHYSICAL && !s.archived)
  if (serviceFilter.value) {
    const lower = serviceFilter.value.toLowerCase()
    services = services.filter(s => s.name?.toLowerCase().includes(lower))
  }
  return services
})
const onServiceFilter = (val, update) => {
  update(() => { serviceFilter.value = val.toLowerCase() })
}
const projects = ref([])

onMounted(async () => {
  warehouses.value = (await planningStore.fetchWarehousesAsync({ all: true })).data
  await planningStore.fetchEquipmentsAsync({ all: true }, true)
  projects.value = await planningStore.fetchProjectsAsync({ all: true }, true)
})

const destinationColumnClass = computed(() => {
  if (isMobile.value) return 'col-12'
  return isSplitTransfer.value ? 'col-4' : 'col-6'
})

const onMovementTypeChange = (val) => {
  editItem.value.movement_type = val
  if (val === planningStore.warehouseMovementTypeEnum.TRANSFER) {
    editItem.value.destination_scheduled_at = editItem.value.scheduled_at
  } else {
    editItem.value.destination_warehouse = null
    editItem.value.destination_scheduled_at = null
  }
}

const onSourceWarehouseChange = (val) => {
  if (editItem.value.destination_warehouse === val) {
    editItem.value.destination_warehouse = null
  }
}

// A serialized (single-unit) item always moves one unit; an adjustment can only remove it.
watch([isSerialized, isAdjustment], () => {
  if (isSerialized.value) {
    editItem.value.quantity = isAdjustment.value ? -1 : 1
  }
}, { immediate: true })

const warehouseName = (id) => warehouses.value.find(w => w.id === id)?.name || ''

// Signed change this movement applies to the source warehouse stock.
const sourceDelta = () => {
  const enums = planningStore.warehouseMovementTypeEnum
  const quantity = Number(editItem.value.quantity)
  switch (editItem.value.movement_type) {
    case enums.UNLOAD:
    case enums.TRANSFER:
      return -quantity
    case enums.ADJUSTMENT:
      return quantity
    default:
      return 0
  }
}

// Generic adjustments may be intentionally negative, so only guard them for serialized units.
const shouldCheckStock = () => {
  const enums = planningStore.warehouseMovementTypeEnum
  if (editItem.value.movement_type === enums.ADJUSTMENT) return isSerialized.value
  return editItem.value.movement_type === enums.UNLOAD || isTransfer.value
}

// A serialized unit exists in exactly one place, so it can't be checked in while already in stock.
const isSerializedCheckIn = () =>
  isSerialized.value && editItem.value.movement_type === planningStore.warehouseMovementTypeEnum.LOAD

const onUpdate = async () => {
  if (!editItem.value.id && (isSerializedCheckIn() || shouldCheckStock())) {
    const availability = await loadAvailabilityAtDay([editItem.value.service], editItem.value.scheduled_at)

    if (isSerializedCheckIn()) {
      const existing = Object.entries(availability).find(([key, qty]) => {
        const [, serviceId] = key.split('_')
        return Number(serviceId) === editItem.value.service && Number(qty) >= 1
      })
      if (existing) {
        Dialog.create({
          title: i18n.t('common.warning'),
          message: i18n.t('warning.serializedUnitAlreadyInStock', { warehouse: warehouseName(Number(existing[0].split('_')[0])) }),
          persistent: true,
        })
        return
      }
    } else {
      const available = availability[`${editItem.value.warehouse}_${editItem.value.service}`] ?? 0
      if (available + sourceDelta() < 0) {
        if (isSerialized.value) {
          Dialog.create({
            title: i18n.t('common.warning'),
            message: i18n.t('warning.serializedUnitNotAvailable', { warehouse: warehouseName(editItem.value.warehouse) }),
            persistent: true,
          })
          return
        }
        Dialog.create({
          title: i18n.t('common.warning'),
          message: i18n.t('warning.stockBelowZeroOnCheckout', { available, warehouse: warehouseName(editItem.value.warehouse) }),
          cancel: true,
          persistent: true,
        }).onOk(() => confirmSplitThenSave())
        return
      }
    }
  }
  await confirmSplitThenSave()
}

const confirmSplitThenSave = async () => {
  if (!editItem.value.id && isSplitTransfer.value) {
    Dialog.create({
      title: i18n.t('common.info'),
      message: i18n.t('info.transferSplitInfo', {
        source: warehouseName(editItem.value.warehouse),
        destination: warehouseName(editItem.value.destination_warehouse),
      }),
      cancel: true,
      persistent: true,
    }).onOk(() => doSave())
    return
  }
  await doSave()
}

const doSave = async () => {
  isUpdating.value = true
  try {
    const updateItem = { ...editItem.value }
    if (updateItem.scheduled_at) {
      updateItem.scheduled_at = dayjs(updateItem.scheduled_at).toISOString()
    }
    if (isTransfer.value && updateItem.destination_scheduled_at) {
      updateItem.destination_scheduled_at = dayjs(updateItem.destination_scheduled_at).toISOString()
    }
    const { data: saved } = await planningStore.upsertWarehouseMovementAsync(updateItem)
    notifySuccess(editItem.value.id ? i18n.t('info.updatedSuccessfully') : i18n.t('info.createdSuccessfully'))
    emit('update', saved)
  } catch (error) {
    console.error(error)
    notifyError(i18n.t('error.saveFailed'))
  } finally {
    isUpdating.value = false
  }
}

const showDeleteDialog = ref(false)

const onConfirmDelete = () => { showDeleteDialog.value = true }

const onDeleteConfirmed = async () => {
  try {
    await planningStore.deleteWarehouseMovementAsync(editItem.value.id)
    notifySuccess(i18n.t('info.deletedSuccessfully'))
    emit('delete')
  } catch (error) {
    console.error(error)
    notifyError(i18n.t('error.deleteFailed'))
  }
}

const emit = defineEmits(['update', 'cancel', 'delete'])
useNavigator(() => emit('cancel'))
useEscapeClose(isUpdated, () => emit('cancel'))
const onCancel = () => emit('cancel')

const step1HasError = computed(() => !editItem.value.service || editItem.value.movement_type == null || !editItem.value.quantity || !editItem.value.warehouse || !editItem.value.scheduled_at)
const step2HasError = computed(() => isTransfer.value && (
  !editItem.value.destination_warehouse || !editItem.value.destination_scheduled_at || (isSplitTransfer.value && !editItem.value.transit_warehouse)
))
const hasErrors = computed(() => step1HasError.value || step2HasError.value)

</script>
<template>
  <q-card v-drag-dialog="!isMobile" class="dialog-card-flex">
    <navigate-back
      :text="editItem?.id ? i18n.t('common.editMovement') : i18n.t('common.addMovement')"
      @cancel="onCancel"
    />
    <q-card-section class="scroll col q-pa-none dialog-card-content-scroll" :class="{ 'dialog-card-content': editItem?.id }">
      <q-card-section class="q-px-sm q-pt-none">
        <q-form ref="form">
          <q-stepper v-model="step" class="q-py-none">
            <q-step
              :name="1"
              :title="i18n.t('common.basicInfo')"
              :icon="wasAttempted(1) && step1HasError ? 'mdi-alert' : 'mdi-folder-plus'"
              :done="!!editItem.id || step > 1"
              :header-nav="!!editItem.id || step > 1"
              :error="wasAttempted(1) && step1HasError"
              error-color="negative"
            >
              <div class="row q-col-gutter-sm">
                <q-select
                  :model-value="editItem.movement_type"
                  :class="isMobile ? 'col-12' : 'col-3'"
                  :label="i18n.t('common.type')"
                  option-value="id"
                  option-label="name"
                  :options="localizedMovementTypes"
                  emit-value
                  map-options
                  required
                  :rules="[val => val != null || i18n.t('common.required')]"
                  @update:model-value="onMovementTypeChange"
                />
                <service-selector
                  v-model="editItem.service"
                  :class="isMobile ? 'col-12' : 'col-6'"
                  :label="i18n.t('common.service')"
                  :options="physicalServices"
                  :rules="[val => val != null || i18n.t('common.required')]"
                  use-input
                  :input-debounce="webSiteStore.getInputDebounce()"
                  @filter="onServiceFilter"
                />
                <q-input
                  v-model="editItem.scheduled_at"
                  :class="isMobile ? 'col-12' : 'col-3'"
                  :label="i18n.t('common.scheduledAt')"
                  type="datetime-local"
                  required
                  :rules="[val => !!val || i18n.t('common.required')]"
                />
                <warehouse-selector
                  v-if="!props.warehouse"
                  :model-value="editItem.warehouse"
                  :class="isMobile ? 'col-12' : 'col-3'"
                  required
                  @update:model-value="editItem.warehouse = $event; onSourceWarehouseChange($event)"
                />
                <q-input
                  v-if="!isSerialized"
                  v-model.number="editItem.quantity"
                  :class="isMobile ? 'col-12' : 'col-3'"
                  :label="i18n.t('common.quantity')"
                  type="number"
                  :min="isAdjustment ? undefined : 1"
                  required
                  :rules="[val => val != null && (isAdjustment ? val !== 0 : val >= 1) || i18n.t('common.required')]"
                />
                <q-select
                  v-model="editItem.project"
                  :class="isMobile ? 'col-12' : 'col'"
                  :label="i18n.t('common.project')"
                  option-value="id"
                  option-label="name"
                  :options="projects.filter(p => !p.archived)"
                  emit-value
                  map-options
                  clearable
                />
              </div>
              <stepper-navigation
                :continue="() => { markAttempted(1); form.validate().then(success => !step1HasError && success && (isTransfer ? nextStep() : nextStep(2))) }"
              />
            </q-step>
            <q-step
              v-if="isTransfer"
              :name="2"
              :title="i18n.t('common.destination')"
              :icon="wasAttempted(2) && step2HasError ? 'mdi-alert' : 'mdi-swap-horizontal'"
              :done="!!editItem.id || step > 2"
              :header-nav="!!editItem.id || step > 2"
              :error="wasAttempted(2) && step2HasError"
              error-color="negative"
            >
              <div class="row q-col-gutter-sm">
                <warehouse-selector
                  v-model="editItem.destination_warehouse"
                  :class="destinationColumnClass"
                  :label="i18n.t('common.destinationWarehouse')"
                  :exclude="editItem.warehouse"
                  required
                />
                <q-input
                  v-model="editItem.destination_scheduled_at"
                  :class="destinationColumnClass"
                  :label="i18n.t('common.destinationScheduledAt')"
                  type="datetime-local"
                  :min="editItem.scheduled_at"
                  required
                  :rules="[val => !!val || i18n.t('common.required')]"
                />
                <warehouse-selector
                  v-if="isSplitTransfer"
                  v-model="editItem.transit_warehouse"
                  :class="destinationColumnClass"
                  :label="i18n.t('common.transitWarehouse')"
                  :kind="planningStore.warehouseKindEnum.VIRTUAL"
                  required
                />
              </div>
              <stepper-navigation
                :back="prevStep"
                :continue="() => { markAttempted(2); form.validate().then(success => !step2HasError && success && nextStep()) }"
              />
            </q-step>
            <q-step
              :name="3"
              :title="i18n.t('common.additionalInfo')"
              icon="mdi-information-outline"
              :done="!!editItem.id || step > 3"
              :header-nav="!!editItem.id || step > 3"
            >
              <div class="row q-col-gutter-sm">
                <q-input
                  v-model="editItem.notes"
                  class="col-12"
                  :label="i18n.t('common.notes')"
                  autogrow
                />
              </div>
              <stepper-navigation
                :back="() => (isTransfer ? prevStep() : prevStep(2))"
                :finish="editItem.id ? null : () => { markAttempted(3); form.validate().then(success => !hasErrors && success && onUpdate()) }"
                :finish-label="i18n.t('common.addMovement')"
                :loading="isUpdating"
              />
            </q-step>
          </q-stepper>
        </q-form>
      </q-card-section>
    </q-card-section>
    <q-card-actions v-if="editItem?.id" class="align-center dialog-card-bottom-bar">
      <q-btn
        v-if="canDelete"
        class="large-btn q-mr-sm"
        color="negative"
        :label="i18n.t('common.delete')"
        @click="onConfirmDelete"
      />
      <q-btn
        v-if="isUpdated"
        class="large-btn"
        color="positive"
        :disable="hasErrors"
        :label="i18n.t('common.update')"
        :loading="isUpdating"
        @click="() => form.validate().then(success => !hasErrors && success && onUpdate())"
      />
    </q-card-actions>
    <confirm-dialog
      v-model="showDeleteDialog"
      :message="i18n.t('common.confirmDelete')"
      icon="mdi-delete-outline"
      icon-color="negative"
      @confirm="onDeleteConfirmed"
    />
  </q-card>
</template>
